import { useState } from "react";
import { FileTextIcon, PlusIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { SadieSimulatorPromptChangeDialog } from "@/components/tools/sadie-simulator/SadieSimulatorPromptChangeDialog";
import { SadieSimulatorPromptChangeItem } from "@/components/tools/sadie-simulator/SadieSimulatorPromptChangeItem";
import { SadieSimulatorPromptViewer } from "@/components/tools/sadie-simulator/SadieSimulatorPromptViewer";
import { Button } from "@ui/button";

import { sadieSimulator } from "@/lib/sadieSimulator";
import {
  SadieSimulatorPrompt,
  SadieSimulatorPromptChange,
  SadieSimulatorPromptMiss,
} from "@/types";

const MISS_REASONS: Record<SadieSimulatorPromptMiss["reason"], string> = {
  NOT_FOUND: "isn't in her last turn's prompt, so the change did nothing.",
  ALREADY_THERE: "was already in her last turn's prompt, so it wasn't added.",
  AFTER_NOT_FOUND:
    "went before WHO YOU ARE AND HOW YOU TEXT, since the section it follows wasn't there.",
};

// The PM's edits to Sadie's prompt, for this tester in the simulator only.
// Each one applies to every turn from her next one until it's removed.
export const SadieSimulatorPromptChanges = ({
  changes,
  isSaving,
  isSimulating,
  onSave,
  prompt,
}: {
  changes: SadieSimulatorPromptChange[];
  isSaving: boolean;
  isSimulating: boolean;
  onSave: (changes: SadieSimulatorPromptChange[]) => Promise<void>;
  prompt: SadieSimulatorPrompt | null;
}) => {
  /* React Hooks */
  // The change being edited, by its place in the list, or NEW for one being
  // added.
  const [editing, setEditing] = useState<number | "NEW" | null>(null);
  const [isViewing, setIsViewing] = useState(false);

  /* Helpers */
  const status = () => {
    if (!isSimulating) return "Start over to change Sadie's prompt.";
    if (!prompt) return "Her sections show up here after her first reply.";
    return "Each change applies to every turn from her next one. Only here, never to real workers.";
  };

  // What the section a removal takes out said, before that change.
  const removedTextAt = (index: number) => {
    if (!prompt) return "";
    const before = sadieSimulator.withPromptChanges(
      prompt.sections,
      changes.slice(0, index),
    );
    const section = sadieSimulator.sectionFor(before, changes[index].heading);
    return section ? section.text : "";
  };

  // The sections an edit picks from. Changes apply in order, so an edit sees
  // only the ones before it and a new change sees them all.
  const sectionsForEditing = (sections: SadieSimulatorPrompt["sections"]) =>
    sadieSimulator.withPromptChanges(
      sections,
      typeof editing === "number" ? changes.slice(0, editing) : changes,
    );

  /* Handlers */
  // Closed only once it saved, so a failed save keeps the edit to retry. An
  // edit takes the old change's place in the list.
  const handleSaveChange = async (change: SadieSimulatorPromptChange) => {
    const next =
      typeof editing === "number"
        ? changes.map((c, i) => (i === editing ? change : c))
        : [...changes, change];
    try {
      await onSave(next);
    } catch {
      return;
    }
    setEditing(null);
  };

  const handleRemove = (index: number) => {
    void onSave(changes.filter((_, i) => i !== index)).catch(() => undefined);
  };

  /* Render */
  return (
    <Column className="gap-2">
      <Row className="items-center justify-between gap-2">
        <Text className="text-sm font-medium text-slate-700">
          Prompt changes
        </Text>
        <Button
          disabled={!isSimulating || !prompt || isSaving}
          onClick={() => {
            setEditing("NEW");
          }}
          size="sm"
          variant="outline"
        >
          <PlusIcon className="h-4 w-4" />
          Add a change
        </Button>
      </Row>
      <Text className="text-xs font-normal text-slate-500">{status()}</Text>
      <Button
        disabled={!prompt}
        onClick={() => {
          setIsViewing(true);
        }}
        size="sm"
        variant="outline"
      >
        <FileTextIcon className="h-4 w-4" />
        View the full prompt
      </Button>
      {changes.map((change, index) => (
        <SadieSimulatorPromptChangeItem
          change={change}
          isDisabled={isSaving}
          key={`${change.heading}-${String(index)}`}
          onEdit={() => {
            setEditing(index);
          }}
          onRemove={() => {
            handleRemove(index);
          }}
          removedText={removedTextAt(index)}
        />
      ))}
      {prompt?.misses.map((miss) => (
        <Text
          className="text-xs font-normal text-amber-700"
          key={`${miss.heading}-${miss.reason}`}
        >
          {`${miss.heading} ${MISS_REASONS[miss.reason]}`}
        </Text>
      ))}
      {editing !== null && prompt ? (
        <SadieSimulatorPromptChangeDialog
          initialChange={
            typeof editing === "number" ? changes[editing] : undefined
          }
          isSaving={isSaving}
          onClose={() => {
            setEditing(null);
          }}
          onSave={handleSaveChange}
          sections={sectionsForEditing(prompt.sections)}
        />
      ) : null}
      {isViewing && prompt ? (
        <SadieSimulatorPromptViewer
          changes={changes}
          onClose={() => {
            setIsViewing(false);
          }}
          prompt={prompt}
        />
      ) : null}
    </Column>
  );
};
